import React, { useState } from 'react';
import { Container, Form, Button, Card, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import '../styles/Auth.css';

export const Login: React.FC = () => {
  const MIN_LOADING_MS = 450;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLocalization();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const loadingStartedAt = Date.now();

    try {
      const response = await authService.login({ username, password });

      if (response.success && response.token && response.user) {
        login(response.token, response.user);
        navigate('/');
      } else {
        setError(response.message || t('loginFailed'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('errorDuringLogin'));
    } finally {
      const elapsed = Date.now() - loadingStartedAt;
      const remaining = MIN_LOADING_MS - elapsed;
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <Container className="auth-page-shell">
          <div className="auth-card-wrapper auth-loading-wrapper">
            <Card className="auth-card auth-loading-card" aria-live="polite">
              <Card.Body>
                <div className="auth-loading-brand page-skeleton-shimmer" aria-hidden="true" />
                <div className="auth-loading-title page-skeleton-shimmer" aria-hidden="true" />
                <p className="auth-loading-subtitle mb-4">{t('loggingIn')}</p>

                <div className="auth-loading-label page-skeleton-shimmer" aria-hidden="true" />
                <div className="auth-loading-input page-skeleton-shimmer mb-3" aria-hidden="true" />

                <div className="auth-loading-label page-skeleton-shimmer" aria-hidden="true" />
                <div className="auth-loading-input page-skeleton-shimmer mb-4" aria-hidden="true" />

                <div className="auth-loading-button page-skeleton-shimmer" aria-hidden="true" />
              </Card.Body>
            </Card>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <Container className="auth-page-shell">
        <div className="auth-card-wrapper">
          <Card className="auth-card">
            <Card.Body>
              <div className="auth-brand-chip">{t('appTitle')}</div>
              <h2 className="text-center auth-title mb-1">{t('login')}</h2>
              <p className="text-center auth-subtitle mb-4">Secure access to your trust account dashboard</p>

              {error && <Alert variant="danger">{error}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('username')}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t('username')}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    {t('defaultAdminUsernameLabel')}: <code>admin</code>
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>{t('password')}</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder={t('password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    {t('defaultAdminPasswordLabel')}: <code>Admin@123</code>
                  </Form.Text>
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 mb-3 auth-submit-btn"
                  disabled={loading}
                >
                  {loading ? t('loggingIn') : t('login')}
                </Button>
              </Form>

              <div className="text-center mt-3 auth-footer-copy">
                <p className="mb-0">{t('contactAdminToRegister')}</p>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default Login;
